/**
 * useEmissionsFormatter Hook
 *
 * Provides formatting functions for emissions values based on organization settings.
 * Fetches display settings and returns memoized formatter functions.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetSettingsQuery } from '../api/emissions';
import {
  createEmissionsFormatter,
  getDisplaySettingsFromEmissionSettings,
  formatEmission,
  formatEmissionCompact,
  DEFAULT_DISPLAY_SETTINGS,
  type DisplaySettings,
  type FormattedEmission,
} from '../utils/emissionsFormatter';

export interface UseEmissionsFormatterOptions {
  /** Override initiativeId (uses URL param by default) */
  initiativeId?: string;
  /** Skip fetching settings (use defaults) */
  skip?: boolean;
}

export interface UseEmissionsFormatterResult {
  /** Format emission value with full precision */
  format: (valueInKg: number | null | undefined) => FormattedEmission;
  /** Format emission value with auto-scaling for large numbers */
  formatCompact: (valueInKg: number | null | undefined) => FormattedEmission;
  /** Current display settings being used */
  settings: DisplaySettings;
  /** Whether settings are still loading */
  isLoading: boolean;
  /** Whether using default settings (no custom settings configured) */
  isUsingDefaults: boolean;
  /** Error from settings API call, if any */
  error: unknown;
  /** Whether settings API call failed */
  isError: boolean;
}

/**
 * Hook to format emissions values based on organization display settings
 *
 * @example
 * ```tsx
 * const { format, formatCompact, isLoading } = useEmissionsFormatter();
 *
 * // Full format: "1,234.56 tCO₂e"
 * const result = format(1234560); // valueInKg
 * <span>{result.display}</span>
 *
 * // Compact format for large values: "1.2 MtCO₂e"
 * const compact = formatCompact(1234560000);
 * <span>{compact.display}</span>
 * ```
 */
export const useEmissionsFormatter = (
  options: UseEmissionsFormatterOptions = {}
): UseEmissionsFormatterResult => {
  const { initiativeId: urlInitiativeId } = useParams<{ initiativeId: string }>();
  const initiativeId = options.initiativeId ?? urlInitiativeId;

  const { data: emissionSettings, isLoading, error, isError } = useGetSettingsQuery(
    { initiativeId: initiativeId ?? '' },
    { skip: options.skip || !initiativeId }
  );

  // Log API errors in development
  if (isError && error && process.env.NODE_ENV === 'development') {
    console.warn('[useEmissionsFormatter] Failed to load emission settings:', error);
  }

  const displaySettings = useMemo(
    () => getDisplaySettingsFromEmissionSettings(emissionSettings),
    [emissionSettings]
  );

  const formatter = useMemo(() => createEmissionsFormatter(displaySettings), [displaySettings]);

  // Using defaults when no settings were loaded (not when settings happen to match defaults)
  const isUsingDefaults = !emissionSettings;

  return {
    format: formatter.format,
    formatCompact: formatter.formatCompact,
    settings: displaySettings,
    isLoading,
    isUsingDefaults,
    error,
    isError,
  };
};

/**
 * Standalone formatting functions for use outside React components
 * Uses default settings - for components that don't need dynamic settings
 */
export { formatEmission, formatEmissionCompact, DEFAULT_DISPLAY_SETTINGS };
